import { Eye, Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { openEmailPreview } from '~/pages/messages/openEmailPreview';
import type { OrderReminderMessage } from '~/pages/messages/Messages';
import {
  calculateOrderTotal,
  formatMoney,
  formatOrderTotal,
  priceUnitLabel,
} from '~/utils';

export function OrderReminderCard({
  msg,
}: Readonly<{ msg: OrderReminderMessage }>) {
  const { t } = useTranslation('messages');
  const navigate = useNavigate();
  const { emailHtmlBody } = msg;
  const items = msg.order?.items ?? [];
  const currency = msg.order?.currency ?? undefined;
  const totalAmount = msg.order
    ? formatOrderTotal(
        calculateOrderTotal(msg.order),
        t('messages.not_available'),
        currency
      )
    : '';

  return (
    <div className="flex flex-col items-center gap-1">
      <span className="text-xs text-neut-400">{msg.timestamp}</span>
      <div className="w-full max-w-md border border-neut-border rounded-lg overflow-hidden shadow-sm">
        <div className="flex items-center justify-between px-4 py-2.5 bg-neut-bg-alt border-b border-neut-border">
          <span className="text-sm font-semibold text-neut-prim">
            {t('messages.order_reminder.title')}
          </span>
          <span className="text-xs font-medium text-prim bg-prim-100 px-2 py-0.5 rounded-full">
            #{msg.orderNumber}
          </span>
        </div>
        <div className="px-4 py-3 bg-neut-bg-alt flex flex-col gap-2">
          <p className="text-xs text-neut-500">
            {t('messages.order_reminder.sent_to', {
              recipient: msg.recipient,
            })}
          </p>

          {items.map((item) => {
            const lineTotal = formatOrderTotal(
              calculateOrderTotal({ items: [item] }),
              t('messages.not_available'),
              currency
            );
            const description = `${item.platformProductsLengths.platformProduct.name} - ${item.platformProductsLengths.lengthDisplayValue}`;
            return (
              <div key={description} className="flex flex-col gap-0.5">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-neut-prim">
                    {description}{' '}
                    <span className="text-xs bg-status-info-50 text-status-info-600 px-1.5 py-0.5 rounded">
                      {item.quantity.unit}
                    </span>
                  </div>
                  <span className="text-sm font-semibold text-neut-prim">
                    {lineTotal}
                  </span>
                </div>
                <p className="text-xs text-neut-500">
                  {item.quantity.value} {item.quantity.unit} x{' '}
                  {item.effectivePrice == null
                    ? t('messages.not_available')
                    : `${formatMoney(item.effectivePrice)}/${priceUnitLabel(item.quantity.unit)}`}
                </p>
              </div>
            );
          })}

          <div className="flex items-center justify-between border-t border-neut-100 pt-2 mt-1">
            <span className="text-sm font-medium text-neut-prim">
              {t('messages.order_reminder.total_amount')}
            </span>
            <span className="text-sm font-bold text-neut-prim">
              {totalAmount}
            </span>
          </div>
          <p className="text-xs text-neut-500">{msg.address}</p>
          <div className="flex gap-2 mt-1">
            <button
              disabled={!msg.order?.id}
              onClick={() => {
                if (msg.order) navigate(`/orders/view/${msg.order.id}`);
              }}
              className="flex items-center gap-1.5 text-xs text-neut-600 border border-neut-200 rounded px-3 py-1.5 hover:bg-neut-50 transition-colors"
            >
              <Eye size={12} />
              {t('messages.order_reminder.view_order')}
            </button>
            {emailHtmlBody && (
              <button
                onClick={() => openEmailPreview(emailHtmlBody)}
                className="flex items-center gap-1.5 text-xs text-neut-600 border border-neut-200 rounded px-3 py-1.5 hover:bg-neut-50 transition-colors"
              >
                <Mail size={12} />
                {t('messages.order_reminder.email')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
